/* eslint-disable no-shadow, class-methods-use-this, react-redux/mapStateToProps-prefer-parameters-names */
import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Footer from 'wanderu-component-footer';
import Intercom from 'wanderu-component-intercom';
import NotFound from 'wanderu-component-not-found';
import { BusLoader } from 'wanderu-components';
import { placeGuideSelectors } from 'wanderu-ducks-seo';
import {
    i18nCoordinator,
    selectLocale,
} from 'wanderu-i18n';

import placeGuideConfig from '../../../config/placeguide.locale';
import PlaceGuideComponent from '../../components/PlaceGuide/PlaceGuide';
import PlaceGuideMeta from '../../components/PlaceGuide/PlaceGuideMeta/PlaceGuideMeta';

class PlaceGuideContainer extends Component {
    static async serverFetchData(opts) {
        const {
            i18nCoordinator,
            getState,
            placeId,
        } = opts;

        await i18nCoordinator.registerTranslatableApp({
            ...placeGuideConfig,
            translationFetchFn: () => {
                return placeGuideSelectors.selectContent(getState(), placeId);
            },
        });
    }

    async componentDidMount() {
        const {
            notFound,
            locale,
            content,
        } = this.props;

        if (notFound) {
            return;
        }

        if (content) {
            i18nCoordinator.updateTranslations(locale, content, placeGuideConfig.namespace);
            this.forceUpdate();
        }

        await i18nCoordinator.registerTranslatableApp({
            ...placeGuideConfig,
            translationFetchFn: () => {},
        });
    }

    async componentDidUpdate(prevProps) {
        const {
            locale,
            content,
        } = this.props;

        if (prevProps.isFetching && !this.props.isFetching) {
            i18nCoordinator.updateTranslations(locale, content, placeGuideConfig.namespace);
            this.forceUpdate(); // the translations changed, but there's nothing that will cause a re-render, so we force it.
        }
    }

    componentWillUnmount() {
        i18nCoordinator.unregisterTranslatableApp(placeGuideConfig.namespace);
    }

    render() {
        const {
            alternateTransModes,
            c2cRoutesData,
            carrierData,
            cheapestTicketData,
            content,
            error,
            hideBlogBlurbSection = false,
            isFetching,
            locale,
            location,
            mostPopularRoutes,
            noIndex,
            noIndexLocale,
            notFound,
            popularRoutesData,
            staticContext,
            structuredData,
            blurbSectionContent,
        } = this.props;

        if (staticContext) {
            staticContext.cache = {
                secondsFromNow: 60 * 60 * 24 * 7,
            };

            if (staticContext.url) {
                return null;
            } if (notFound) {
                staticContext.status = 404;
            } else if (error) {
                staticContext.status = error.status;
                staticContext.cache = undefined;
            } else if (alternateTransModes) {
                /* igor-priority order */
                const urlSlug = alternateTransModes.bus
                    || alternateTransModes.train
                    || alternateTransModes.ferry;

                staticContext.status = 301;
                staticContext.url = `/${locale}${urlSlug}/${location.search}`;

                return null;
            }
        }

        if (notFound || error) {
            return <NotFound status={staticContext?.status || error?.status} />;
        }

        if (isFetching || !content) {
            return (
                <BusLoader large />
            );
        }

        const {
            cityDescription,
            popularStationsDescription,
        } = content;
        const {
            blogPosts,
        } = blurbSectionContent;

        return (
            <>
                <PlaceGuideMeta
                    canonicalPath={content.canonicalSlug}
                    data={cheapestTicketData}
                    noIndex={noIndex}
                    noIndexLocale={noIndexLocale}
                    structuredData={structuredData}
                />
                <PlaceGuideComponent
                    {...this.props}
                    c2cRoutesData={popularRoutesData
                        ? mostPopularRoutes
                        : c2cRoutesData}
                    showBlogBlurbSection={
                        !hideBlogBlurbSection && locale.includes('en') && !!blogPosts.length
                    }
                    showCarriers={!!carrierData}
                    showCityBlurbSection={!!cityDescription}
                    showStationBlurbSection={!!popularStationsDescription}
                />
                <Footer
                    limitLinkFlow
                />
                <Intercom/>
            </>
        );
    }
}

function mapStateToProps(appState, ownProps) {
    const {
        match,
        placeGuideId,
    } = ownProps;
    const {
        params,
    } = match;

    const locale = selectLocale(appState);
    const content = placeGuideSelectors.selectContent(appState, placeGuideId);
    const alternateTransModes = placeGuideSelectors.selectAlternateTransModes(
        appState,
        placeGuideId,
    );

    if (alternateTransModes) {
        return {
            alternateTransModes,
            locale,
        };
    }

    return {
        content,
        locale,
        breadcrumbData: placeGuideSelectors.selectBreadcrumbData(appState, placeGuideId, params),
        routeData: placeGuideSelectors.selectRouteData(appState, placeGuideId),
        cheapestTicketData: placeGuideSelectors.selectCheapestTicketData(appState, placeGuideId),
        carrierData: placeGuideSelectors.selectPlaceGuideCarrierData(appState, placeGuideId),
        c2cRoutesData: placeGuideSelectors.selectC2CData(appState, placeGuideId),
        infoSectionContent: placeGuideSelectors.selectInfoSectionContent(appState, placeGuideId),
        locationData: placeGuideSelectors.selectLocationData(appState, placeGuideId),
        longFormContent: placeGuideSelectors.selectLongFormContent(appState, placeGuideId),
        mostPopularRoutes: placeGuideSelectors.selectMostPopularRoutes(appState, placeGuideId),
        noIndex: placeGuideSelectors.selectNoIndex(appState, placeGuideId),
        noIndexLocale: placeGuideSelectors.selectNoIndexLocale(appState, placeGuideId),
        popularStationsData: placeGuideSelectors.selectPopularStationsData(appState, placeGuideId),
        quickviewData: placeGuideSelectors.selectQuickviewData(appState, placeGuideId),
        structuredData: placeGuideSelectors.selectStructuredData(appState, placeGuideId, params),
        interlinks: placeGuideSelectors.selectInterlinks(appState, placeGuideId),
        stats: placeGuideSelectors.selectStats(appState, placeGuideId),
        blurbSectionContent: placeGuideSelectors.selectBlurbSectionContent(appState, placeGuideId),
    };
}

PlaceGuideContainer.propTypes = {
    // When true, suppresses the blog blurb section regardless of locale/content.
    // Used by CityGuide while blog posts migrate to nexus (see ui-react#1070).
    hideBlogBlurbSection: PropTypes.bool,
};

export {
    PlaceGuideContainer,
    mapStateToProps,
};
export default connect(mapStateToProps)(PlaceGuideContainer);
